import { API_BASE_URL, apiClient } from '@/services/api-client';
import { saveBlobAsFile } from '@/lib/download';

export function evidenceExtension(contentType: string): string {
  const type = String(contentType || '').toLowerCase();
  if (type.includes('pdf')) return '.pdf';
  if (type.includes('png')) return '.png';
  if (type.includes('webp')) return '.webp';
  if (type.includes('jpeg') || type.includes('jpg')) return '.jpg';
  return '';
}

export function evidenceDownloadName(label: string, contentType: string): string {
  const base = String(label || 'documento').replace(/[^a-zA-Z0-9áéíóúñ\- ]/gi, '_').trim() || 'documento';
  const ext = evidenceExtension(contentType);
  return ext && !base.toLowerCase().endsWith(ext) ? `${base}${ext}` : base;
}

async function fetchEvidenceBlob(
  token: string,
  url: string
): Promise<{ blob: Blob; contentType: string } | { error: string; status: number }> {
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    let msg = 'No se pudo abrir el documento';
    try {
      const err = (await res.json()) as { error?: string; message?: string };
      msg = err.error || err.message || msg;
    } catch {
      /* ignore */
    }
    return { error: msg, status: res.status };
  }
  const blob = await res.blob();
  return { blob, contentType: res.headers.get('content-type') || blob.type || '' };
}

/** Descarga anverso/reverso/documento extra ya descifrado. No cachea el blob. */
export async function fetchLicenseEvidence(
  orderId: string,
  field: string
): Promise<{ blob: Blob; contentType: string }> {
  const token = await apiClient.getValidToken();
  if (!token) throw new Error('Sesión no válida');

  const primary =
    `${API_BASE_URL}/ops/license-orders/${encodeURIComponent(orderId)}` +
    `/evidence?field=${encodeURIComponent(field)}`;
  const fallback =
    `${API_BASE_URL}/license-orders?orderId=${encodeURIComponent(orderId)}` +
    `&field=${encodeURIComponent(field)}`;

  let result = await fetchEvidenceBlob(token, primary);
  if ('error' in result && result.status === 404) {
    result = await fetchEvidenceBlob(token, fallback);
  }
  if ('error' in result) throw new Error(result.error);
  return result;
}

export { saveBlobAsFile };

/** Abre anverso/reverso/psicofísico descifrado en una pestaña. No cachea el blob. */
export async function openLicenseEvidence(
  orderId: string,
  field: string,
  label: string
): Promise<void> {
  const preview = window.open('about:blank', '_blank');
  try {
    const result = await fetchLicenseEvidence(orderId, field);
    const objectUrl = URL.createObjectURL(result.blob);
    if (preview) {
      preview.location.replace(objectUrl);
    } else {
      saveBlobAsFile(result.blob, evidenceDownloadName(label, result.contentType));
    }
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (error) {
    preview?.close();
    throw error;
  }
}

export async function downloadLicenseEvidence(
  orderId: string,
  field: string,
  fileBase: string
): Promise<void> {
  const result = await fetchLicenseEvidence(orderId, field);
  saveBlobAsFile(result.blob, evidenceDownloadName(fileBase, result.contentType));
}
